/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { ICocMapViewerData } from './coc-map-viewer.data';
import { ICocMapViewerProps } from './coc-map-viewer.props.autogenerated';
import { getAttributeValuesAsync, getByIdsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { format, IContext, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { IAllAddresses, StatusTypes } from '../../themes/b2b/utilities/types/buybox';
import addProductWithOrderAttributes, { IAllAddressesWithDocs } from '../../themes/b2b/utilities/buybox/add-product-with-order-attributes';
import checkDuplicateAtCart from '../../themes/b2b/utilities/buybox/duplicate-address-at-cart';
import { PropertyType } from '../../themes/b2b/utilities/propertyTypeEnum';
import { ICommunity, IDocumentSearchCriteria } from '../../customProxy/retailExtension/DataServiceEntities.g';
import { getCommunityAsync, getDocumentAvailabilityAsync } from '../../customProxy/retailExtension/DataActionExtension.g';
import { v4 as uuid } from 'uuid';

export interface ICOLPOST {
    id: number;
    product: string;
    operation: string;
    rollNumber: any[];
    legals: any[];
    layerlist: any[];
    gmlFilter: any[];
    queryValue: any[];
    boundaryId: any[];
    outputFormat: any[];
    outputCS: any[];
    areaSQKM: any[];
    URL: any[];
    communityNames: string;
    ebpFileNames: string;
    sections: string;
    splitAddresses: string;
    extent: any[];
    sectionDataFormat: any[];
}

export interface IGI {
    COL_POST: ICOLPOST;
    GIS_POST: ICOLPOST;
}

export interface IMessage {
    GIS: IGI[];
}

export interface IIFrameInterface {
    message: IMessage;
}
export interface ICocMapViewerViewState {
    errorMessages: string[];
    isLoading: boolean;
}

export interface ICocMapViewerViewProps extends ICocMapViewerProps<ICocMapViewerData> {
    errorMessages: string[];
    isLoading: boolean;
    resetErrors: () => void;
}

/**
 *
 * CocMapViewer component
 * @extends {React.PureComponent<ICocMapViewerProps<ICocMapViewerData>>}
 */
class CocMapViewer extends React.PureComponent<ICocMapViewerProps<ICocMapViewerData>, ICocMapViewerViewState> {
    constructor(props: ICocMapViewerViewProps) {
        super(props);
        this._addToCart = this._addToCart.bind(this);
        this.state = {
            isLoading: false,
            errorMessages: []
        };
    }

    private handleMessageEvent = async (event: MessageEvent) => {
        const data = event.data;
        const { productNotAdded } = this.props.resources;
        try {
            const IframeMockData: IIFrameInterface = JSON.parse(data || '');
            const productList: ICOLPOST[] = IframeMockData.message.GIS.map(gis =>
                gis.COL_POST ? { ...gis.COL_POST } : { ...gis.GIS_POST }
            );
            if (productList.length > 0) {
                this.setState({
                    isLoading: true,
                    errorMessages: []
                });

                await this._addToCart(productList);
            }
        } catch (error) {
            const errorText = productNotAdded.replace('{data}', data);
            this.props.context.telemetry.error(`${errorText} ${error}`);
        }
    };

    public componentDidMount() {
        window.addEventListener('message', this.handleMessageEvent);
    }
    public componentWillUnmount() {
        // you need to unbind the same listener that was binded.
        window.removeEventListener('message', this.handleMessageEvent);
    }
    public _addToCart = async (productList: ICOLPOST[]) => {
        const errorMessages: string[] = [];
        const {
            duplicateInCartErrorMessage,
            productNotFoundErrorMessage,
            somethingWentWrongErrorMessage,
            loginRequiredErrorMessage
        } = this.props.resources;
        const { channelId } = this.props.context.request.apiSettings;
        const iframeProductsIds = productList.map(product => Number(product.product));
        const ctx: IContext = { callerContext: this.props.context.actionContext };
        const allSimpleProducts: SimpleProduct[] = await getByIdsAsync(ctx, channelId, iframeProductsIds, '');
        const allAddresses: IAllAddressesWithDocs[] = [];
        if (allSimpleProducts.length > 0) {
            const promises = productList.map(async (product: ICOLPOST) => {
                const addedProduct = product;
                const simpleProduct = allSimpleProducts.find(prod => prod.RecordId.toString() === addedProduct.product);
                if (simpleProduct) {
                    const { user } = this.props.context.request;
                    const { showPropertyPA, productTypePA, fileTypePA, tibcoProductNamePA, guestUserPA } = this.props.context.app.config;
                    const fTypeLo = (fileTypePA as string)?.toLowerCase() || '';
                    const attributeValues = await getAttributeValuesAsync(
                        { callerContext: this.props.context.actionContext, queryResultSettings: {} },
                        simpleProduct.RecordId,
                        channelId,
                        0
                    );
                    const showPropertyFieldOption = attributeValues
                        .find(attr => attr.Name === showPropertyPA)
                        ?.TextValue?.toLocaleLowerCase()!;
                    const productType = attributeValues.find(attr => attr.Name === productTypePA)?.TextValue?.toLocaleLowerCase()!;
                    const checkGuestUserProduct = attributeValues.find(x => x.Name === guestUserPA)?.BooleanValue
                        ? false
                        : !user.isAuthenticated;
                    if (!checkGuestUserProduct) {
                        if (showPropertyFieldOption.includes(PropertyType[PropertyType.address])) {
                            {
                                const splitAddresses = addedProduct.splitAddresses.split(',');
                                for (let splitIndex = 0; splitIndex < splitAddresses.length; splitIndex++) {
                                    const verifiedAddressData: IAllAddresses = {
                                        addressVerified: {},
                                        propertyType: 99,
                                        serviceAddress: {},
                                        atsVerified: {},
                                        blockProfileVerified: {},
                                        communityVerified: {},
                                        quadrantVerified: {},
                                        lookForVerified: {},
                                        legalDescriptionVerified: {},
                                        legalDescriptionCondoVerified: {},
                                        sdab: {},
                                        lcsab: {},
                                        relatedProduct: [simpleProduct]
                                    };
                                    const splitedAddress = splitAddresses[splitIndex];
                                    const address = splitedAddress.split('~').filter(item => item !== '');

                                    if (address.length === 5) {
                                        verifiedAddressData.propertyType = PropertyType.address;
                                        verifiedAddressData.addressVerified = {
                                            suite: address[0],
                                            number: address[1],
                                            streetName: address[2],
                                            streetType: address[3],
                                            quadrant: address[4]
                                        };
                                        verifiedAddressData.serviceAddress = {
                                            suite: address[0],
                                            number: address[1],
                                            streetName: address[2],
                                            streetType: address[3],
                                            quadrant: address[4]
                                        };
                                    } else {
                                        verifiedAddressData.propertyType = PropertyType.address;
                                        verifiedAddressData.addressVerified = {
                                            number: address[0],
                                            streetName: address[1],
                                            streetType: address[2],
                                            quadrant: address[3]
                                        };
                                        verifiedAddressData.serviceAddress = {
                                            number: address[0],
                                            streetName: address[1],
                                            streetType: address[2],
                                            quadrant: address[3]
                                        };
                                    }
                                    const requestData: IDocumentSearchCriteria = {
                                        Id: 0,
                                        QueryType: PropertyType.address,
                                        CorrelationId: uuid(),
                                        Address: [
                                            {
                                                Suite: verifiedAddressData.addressVerified.suite,
                                                Number: verifiedAddressData.addressVerified.number,
                                                StreetName: verifiedAddressData.addressVerified.streetName,
                                                StreetType: verifiedAddressData.addressVerified.streetType,
                                                Quadrant: verifiedAddressData.addressVerified.quadrant
                                            }
                                        ],
                                        FileType: attributeValues.find(attr => attr.Name?.toLowerCase() === fTypeLo)?.TextValue || '',
                                        ProductName: attributeValues.find(attr => attr.Name === tibcoProductNamePA)?.TextValue || '',
                                        ProductType: productType.toLowerCase()
                                    };
                                    try {
                                        const result = await getDocumentAvailabilityAsync(ctx, {
                                            ...requestData
                                        });
                                        if (result.IsSuccess) {
                                            const isAlreadyInCart = await checkDuplicateAtCart(
                                                { ...verifiedAddressData.serviceAddress },
                                                this.props.context,
                                                simpleProduct.ItemId
                                            );

                                            if (isAlreadyInCart) {
                                                errorMessages.push(
                                                    format(
                                                        duplicateInCartErrorMessage,
                                                        simpleProduct.Name,
                                                        splitedAddress.replace(/~/g, ' ')
                                                    )
                                                );
                                            } else {
                                                allAddresses.push(verifiedAddressData);
                                            }
                                        } else if (result.Status === '404') {
                                            errorMessages.push(
                                                format(productNotFoundErrorMessage, simpleProduct.Name, splitedAddress.replace(/~/g, ' '))
                                            );
                                        } else {
                                            errorMessages.push(
                                                format(
                                                    somethingWentWrongErrorMessage,
                                                    simpleProduct.Name,
                                                    splitedAddress.replace(/~/g, ' '),
                                                    StatusTypes.IntegrationError,
                                                    requestData.CorrelationId
                                                )
                                            );
                                        }
                                    } catch (error) {
                                        //@ts-ignore
                                        const status = error.status;
                                        if (status === 'Service Unavailable') {
                                            errorMessages.push(
                                                format(
                                                    somethingWentWrongErrorMessage,
                                                    simpleProduct.Name,
                                                    splitedAddress,
                                                    StatusTypes.Canceled,
                                                    requestData.CorrelationId
                                                )
                                            );
                                        } else {
                                            errorMessages.push(
                                                format(
                                                    somethingWentWrongErrorMessage,
                                                    simpleProduct.Name,
                                                    splitedAddress,
                                                    StatusTypes.CRTError,
                                                    requestData.CorrelationId
                                                )
                                            );
                                        }
                                    }
                                }
                            }
                        } else if (showPropertyFieldOption.includes(PropertyType[PropertyType.community])) {
                            {
                                // for document type of product
                                if (productType === PropertyType[PropertyType.document]) {
                                    const splitAddresses = addedProduct.communityNames.split('|');
                                    for (let splitIndex = 0; splitIndex < splitAddresses.length; splitIndex++) {
                                        const verifiedAddressData: IAllAddressesWithDocs = {
                                            addressVerified: {},
                                            propertyType: 99,
                                            serviceAddress: {},
                                            atsVerified: {},
                                            blockProfileVerified: {},
                                            communityVerified: {},
                                            quadrantVerified: {},
                                            lookForVerified: {},
                                            legalDescriptionVerified: {},
                                            legalDescriptionCondoVerified: {},
                                            sdab: {},
                                            lcsab: {},
                                            relatedProduct: [simpleProduct]
                                        };
                                        const splitedAddress = splitAddresses[splitIndex];
                                        const requestData: IDocumentSearchCriteria = {
                                            Id: 0,
                                            QueryType: PropertyType.community,
                                            Communities: [],
                                            CorrelationId: uuid(),
                                            FileType: attributeValues.find(attr => attr.Name?.toLowerCase() === fTypeLo)?.TextValue || '',
                                            ProductName: attributeValues.find(attr => attr.Name === tibcoProductNamePA)?.TextValue || '',
                                            ProductType: productType.toLowerCase()
                                        };
                                        try {
                                            const communityFullName: ICommunity[] = await getCommunityAsync(ctx, splitedAddress.trim());

                                            requestData.Communities = [communityFullName[0].Name!];

                                            const result = await getDocumentAvailabilityAsync(ctx, {
                                                ...requestData
                                            });
                                            if (result.IsSuccess) {
                                                const docList = result.DocumentData?.map(doc => {
                                                    return {
                                                        documentId: doc.DocumentId,
                                                        documentName: doc.DocumentName,
                                                        isChecked: false
                                                    };
                                                });
                                                if (docList && docList?.length > 0) {
                                                    verifiedAddressData.propertyType = PropertyType.community;
                                                    verifiedAddressData.communityVerified = { communityName: requestData.Communities[0] };

                                                    const isAlreadyInCart = await checkDuplicateAtCart(
                                                        {},
                                                        this.props.context,
                                                        simpleProduct.ItemId,
                                                        {
                                                            documentId: docList[0].documentId!,
                                                            searchKey: requestData.Communities[0],
                                                            propertyType: PropertyType.community
                                                        }
                                                    );
                                                    verifiedAddressData.documentList = [
                                                        {
                                                            documentId: docList[0].documentId,
                                                            documentName: docList[0].documentName,
                                                            isChecked: true
                                                        }
                                                    ];

                                                    if (isAlreadyInCart) {
                                                        errorMessages.push(
                                                            format(duplicateInCartErrorMessage, simpleProduct.Name, splitedAddress)
                                                        );
                                                    } else {
                                                        allAddresses.push(verifiedAddressData);
                                                    }
                                                }
                                            } else if (result.Status === '404') {
                                                errorMessages.push(format(productNotFoundErrorMessage, simpleProduct.Name, splitedAddress));
                                            } else {
                                                errorMessages.push(
                                                    format(
                                                        somethingWentWrongErrorMessage,
                                                        simpleProduct.Name,
                                                        splitedAddress,
                                                        StatusTypes.IntegrationError,
                                                        requestData.CorrelationId
                                                    )
                                                );
                                            }
                                        } catch (error) {
                                            //@ts-ignore
                                            const status = error.status;
                                            if (status === 'Service Unavailable') {
                                                errorMessages.push(
                                                    format(
                                                        somethingWentWrongErrorMessage,
                                                        simpleProduct.Name,
                                                        splitedAddress,
                                                        StatusTypes.Canceled,
                                                        requestData.CorrelationId
                                                    )
                                                );
                                            } else {
                                                errorMessages.push(
                                                    format(
                                                        somethingWentWrongErrorMessage,
                                                        simpleProduct.Name,
                                                        splitedAddress,
                                                        StatusTypes.CRTError,
                                                        requestData.CorrelationId
                                                    )
                                                );
                                            }
                                        }
                                    }
                                } else {
                                    const splitAddresses = addedProduct.splitAddresses.split('|');
                                    for (let splitIndex = 0; splitIndex < splitAddresses.length; splitIndex++) {
                                        const verifiedAddressData: IAllAddressesWithDocs = {
                                            addressVerified: {},
                                            propertyType: 99,
                                            serviceAddress: {},
                                            atsVerified: {},
                                            blockProfileVerified: {},
                                            communityVerified: {},
                                            quadrantVerified: {},
                                            lookForVerified: {},
                                            legalDescriptionVerified: {},
                                            legalDescriptionCondoVerified: {},
                                            sdab: {},
                                            lcsab: {},
                                            relatedProduct: [simpleProduct]
                                        };
                                        const splitedAddress = splitAddresses[splitIndex];
                                        const community = splitedAddress.split(',').filter(item => item !== '');
                                        verifiedAddressData.propertyType = PropertyType.community;
                                        verifiedAddressData.communityVerified = { communityName: community[0].trim() };

                                        verifiedAddressData.documentList = [
                                            {
                                                documentId: community[1],
                                                documentName: `${community[4]}.${community[2].split('/')[1]}`,
                                                isChecked: true
                                            }
                                        ];

                                        const isAlreadyInCart = await checkDuplicateAtCart({}, this.props.context, simpleProduct.ItemId, {
                                            documentId: community[1],
                                            searchKey: community[0],
                                            propertyType: PropertyType.community
                                        });

                                        if (isAlreadyInCart) {
                                            errorMessages.push(format(duplicateInCartErrorMessage, simpleProduct.Name, community[0]));
                                        } else {
                                            allAddresses.push(verifiedAddressData);
                                        }
                                    }
                                }
                            }
                        } else if (showPropertyFieldOption.includes(PropertyType[PropertyType.quadrant])) {
                            {
                                const splitAddresses = addedProduct.splitAddresses.split('|');
                                for (let splitIndex = 0; splitIndex < splitAddresses.length; splitIndex++) {
                                    const verifiedAddressData: IAllAddressesWithDocs = {
                                        addressVerified: {},
                                        propertyType: 99,
                                        serviceAddress: {},
                                        atsVerified: {},
                                        blockProfileVerified: {},
                                        communityVerified: {},
                                        quadrantVerified: {},
                                        lookForVerified: {},
                                        legalDescriptionVerified: {},
                                        legalDescriptionCondoVerified: {},
                                        sdab: {},
                                        lcsab: {},
                                        relatedProduct: [simpleProduct]
                                    };
                                    const splitedAddress = splitAddresses[splitIndex];
                                    const quadrant = splitedAddress.split(',').filter(item => item !== '');
                                    verifiedAddressData.propertyType = PropertyType.quadrant;
                                    verifiedAddressData.quadrantVerified = { quadrant: quadrant[0].trim() };

                                    verifiedAddressData.documentList = [
                                        {
                                            documentId: quadrant[1],
                                            documentName: `${quadrant[4]}.${quadrant[2].split('/')[1]}`,
                                            isChecked: true
                                        }
                                    ];

                                    const isAlreadyInCart = await checkDuplicateAtCart({}, this.props.context, simpleProduct.ItemId, {
                                        documentId: quadrant[1],
                                        searchKey: quadrant[0],
                                        propertyType: PropertyType.quadrant
                                    });

                                    if (isAlreadyInCart) {
                                        errorMessages.push(format(duplicateInCartErrorMessage, simpleProduct.Name, quadrant[0]));
                                    } else {
                                        allAddresses.push(verifiedAddressData);
                                    }
                                }
                            }
                        } else if (showPropertyFieldOption.includes(PropertyType[PropertyType.blockProfile].toLowerCase())) {
                            {
                                // for multiple ebp details
                                const splitAddresses = addedProduct.ebpFileNames.split('|');
                                for (let splitIndex = 0; splitIndex < splitAddresses.length; splitIndex++) {
                                    const verifiedAddressData: IAllAddressesWithDocs = {
                                        addressVerified: {},
                                        propertyType: 99,
                                        serviceAddress: {},
                                        atsVerified: {},
                                        blockProfileVerified: {},
                                        communityVerified: {},
                                        quadrantVerified: {},
                                        lookForVerified: {},
                                        legalDescriptionVerified: {},
                                        legalDescriptionCondoVerified: {},
                                        sdab: {},
                                        lcsab: {},
                                        relatedProduct: [simpleProduct]
                                    };
                                    const splitedAddress = splitAddresses[splitIndex];
                                    const block = splitedAddress.split(';').filter(item => item !== '');
                                    verifiedAddressData.propertyType = PropertyType.blockProfile;
                                    verifiedAddressData.blockProfileVerified = { blockProfile: block[0].trim() };
                                    verifiedAddressData.documentList = [
                                        {
                                            documentId: block[1],
                                            documentName: block[2],
                                            isChecked: true
                                        }
                                    ];

                                    const isAlreadyInCart = await checkDuplicateAtCart({}, this.props.context, simpleProduct.ItemId, {
                                        documentId: block[1],
                                        searchKey: block[0],
                                        propertyType: PropertyType.blockProfile,
                                        documentName: block[2]
                                    });

                                    if (isAlreadyInCart) {
                                        errorMessages.push(format(duplicateInCartErrorMessage, simpleProduct.Name, block[0]));
                                    } else {
                                        allAddresses.push(verifiedAddressData);
                                    }
                                }
                            }
                        } else if (showPropertyFieldOption.includes(PropertyType[PropertyType.ats])) {
                            {
                                const splitAddresses = addedProduct.sections.split('|');
                                for (let splitIndex = 0; splitIndex < splitAddresses.length; splitIndex++) {
                                    const verifiedAddressData: IAllAddressesWithDocs = {
                                        addressVerified: {},
                                        propertyType: 99,
                                        serviceAddress: {},
                                        atsVerified: {},
                                        blockProfileVerified: {},
                                        communityVerified: {},
                                        quadrantVerified: {},
                                        lookForVerified: {},
                                        legalDescriptionVerified: {},
                                        legalDescriptionCondoVerified: {},
                                        sdab: {},
                                        lcsab: {},
                                        relatedProduct: [simpleProduct]
                                    };
                                    const splitedAddress = splitAddresses[splitIndex];
                                    const requestData: IDocumentSearchCriteria = {
                                        Id: 0,
                                        CorrelationId: uuid(),
                                        QueryType: PropertyType.ats,
                                        ATSNos: [splitedAddress.trim()],
                                        FileType: attributeValues.find(attr => attr.Name?.toLowerCase() === fTypeLo)?.TextValue || '',
                                        ProductName: attributeValues.find(attr => attr.Name === tibcoProductNamePA)?.TextValue || '',
                                        ProductType: productType.toLowerCase()
                                    };
                                    try {
                                        const result = await getDocumentAvailabilityAsync(ctx, {
                                            ...requestData
                                        });
                                        if (result.IsSuccess) {
                                            const docList = result.DocumentData?.map(doc => {
                                                return {
                                                    documentId: doc.DocumentId,
                                                    documentName: doc.DocumentName,
                                                    isChecked: false
                                                };
                                            });
                                            if (docList && docList?.length > 0) {
                                                verifiedAddressData.propertyType = PropertyType.ats;
                                                verifiedAddressData.atsVerified = { atsValue: splitedAddress.trim() };
                                                verifiedAddressData.documentList = [
                                                    {
                                                        documentId: docList[0].documentId,
                                                        documentName: docList[0].documentName,
                                                        isChecked: true
                                                    }
                                                ];

                                                const isAlreadyInCart = await checkDuplicateAtCart(
                                                    {},
                                                    this.props.context,
                                                    simpleProduct.ItemId,
                                                    {
                                                        documentId: docList[0].documentId!,
                                                        searchKey: splitedAddress.trim(),
                                                        propertyType: PropertyType.ats
                                                    }
                                                );

                                                if (isAlreadyInCart) {
                                                    errorMessages.push(
                                                        format(duplicateInCartErrorMessage, simpleProduct.Name, splitedAddress)
                                                    );
                                                } else {
                                                    allAddresses.push(verifiedAddressData);
                                                }
                                            }
                                        } else if (result.Status === '404') {
                                            errorMessages.push(format(productNotFoundErrorMessage, simpleProduct.Name, splitedAddress));
                                        } else {
                                            errorMessages.push(
                                                format(
                                                    somethingWentWrongErrorMessage,
                                                    simpleProduct.Name,
                                                    splitedAddress,
                                                    StatusTypes.IntegrationError,
                                                    requestData.CorrelationId
                                                )
                                            );
                                        }
                                    } catch (error) {
                                        //@ts-ignore
                                        const status = error.status;
                                        if (status === 'Service Unavailable') {
                                            errorMessages.push(
                                                format(
                                                    somethingWentWrongErrorMessage,
                                                    simpleProduct.Name,
                                                    splitedAddress,
                                                    StatusTypes.Canceled,
                                                    requestData.CorrelationId
                                                )
                                            );
                                        } else {
                                            errorMessages.push(
                                                format(
                                                    somethingWentWrongErrorMessage,
                                                    simpleProduct.Name,
                                                    splitedAddress,
                                                    StatusTypes.CRTError,
                                                    requestData.CorrelationId
                                                )
                                            );
                                        }
                                    }
                                }
                            }
                        }
                    } else {
                        errorMessages.push(format(loginRequiredErrorMessage, simpleProduct.Name));
                    }
                }
            });
            await Promise.all(promises);
            allAddresses.length && (await addProductWithOrderAttributes(allAddresses, this.props.context));
        }
        this.setState({
            isLoading: false,
            errorMessages
        });
    };
    public render(): JSX.Element | null {
        const viewProps: ICocMapViewerViewProps = {
            ...this.props,
            ...this.state,
            resetErrors: () => this.setState({ errorMessages: [] })
        };
        return this.props.renderView(viewProps);
    }
}

export default CocMapViewer;
